import { getAuth, updateProfile } from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { db } from '../firebase';
import { FcHome } from 'react-icons/fc';
import PropertyItem from '../components/PropertyItem';
import Spinner from '../components/Spinner';

export default function Profile() {
  const auth = getAuth();
  const navigate = useNavigate();

  console.log('🔍 Profile Component Rendering');
  console.log('Current User:', auth.currentUser);
  console.log('User Email:', auth.currentUser?.email);

  const [changeDetail, setChangeDetail] = useState(false);
  const [listings, setListings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userChecked, setUserChecked] = useState(false);
  const [formData, setFormData] = useState({
    name: auth.currentUser?.displayName || '',
    email: auth.currentUser?.email || '',
  });

  const { name, email } = formData;

  // Check auth status
  useEffect(() => {
    console.log('🔍 useEffect: Checking auth...');
    const checkAuth = () => {
      if (!auth.currentUser) {
        console.log('❌ No user found, redirecting to sign-in');
        navigate('/sign-in');
      } else {
        console.log('✅ User found:', auth.currentUser.email);
        setUserChecked(true);
      }
    };

    // Small delay to ensure auth is loaded
    const timer = setTimeout(checkAuth, 100);
    return () => clearTimeout(timer);
  }, [auth.currentUser, navigate]);

  // Check if user is admin
  useEffect(() => {
    async function checkAdmin() {
      if (auth.currentUser) {
        try {
          console.log('🔍 Checking admin status...');
          const docRef = doc(db, 'users', auth.currentUser.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const userData = docSnap.data();
            const adminStatus = userData.role === 'admin' || userData.isAdmin === true;
            console.log('Admin status:', adminStatus);
            setIsAdmin(adminStatus);
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
        }
      }
    }

    checkAdmin();
  }, [auth.currentUser]);

  // Fetch user listings
  useEffect(() => {
    async function fetchUserListings() {
      if (!auth.currentUser) {
        console.log('❌ No user, skipping listing fetch');
        setLoading(false);
        return;
      }

      try {
        console.log('🔍 Fetching user listings...');
        const listingRef = collection(db, 'listings');
        const q = query(
          listingRef,
          where('userRef', '==', auth.currentUser.uid),
          orderBy('timestamp', 'desc')
        );
        const querySnap = await getDocs(q);
        let listings = [];
        querySnap.forEach((doc) => {
          listings.push({
            id: doc.id,
            data: doc.data(),
          });
        });
        console.log('✅ Found listings:', listings.length);
        setListings(listings);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching listings:', error);
        setLoading(false);
      }
    }

    if (userChecked) {
      fetchUserListings();
    }
  }, [auth.currentUser?.uid, userChecked]);

  function onLogout() {
    console.log('🔍 Logging out...');
    auth.signOut();
    navigate('/');
  }

  function onChange(e) {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  }

  async function onSubmit() {
    try {
      if (auth.currentUser.displayName !== name) {
        await updateProfile(auth.currentUser, {
          displayName: name,
        });

        const docRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(docRef, {
          name,
        });
      }
      toast.success('Profile details updated');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Could not update the profile details');
    }
  }

  async function onDelete(listingID) {
    if (window.confirm('Are you sure you want to delete?')) {
      try {
        await deleteDoc(doc(db, 'listings', listingID));
        const updatedListings = listings.filter(
          (listing) => listing.id !== listingID
        );
        setListings(updatedListings);
        toast.success('Successfully deleted the listing');
      } catch (error) {
        console.error('Error deleting listing:', error);
        toast.error('Failed to delete listing');
      }
    }
  }

  function onEdit(listingID) {
    navigate(`/edit-listing/${listingID}`);
  }

  // Show spinner while checking user
  if (!userChecked) {
    console.log('⏳ Showing spinner - user not checked yet');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner />
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Show nothing if no user
  if (!auth.currentUser) {
    console.log('❌ No current user, returning null');
    return null;
  }

  console.log('✅ Rendering profile page');

  return (
    <>
      <section className="max-w-6xl mx-auto flex justify-center items-center flex-col">
        <h1 className="text-3xl text-center mt-6 font-bold">My Profile</h1>

        <div className="w-full md:w-[50%] mt-6 px-3">
          {/* Debug Info */}
          <div className="mb-4 p-3 bg-blue-100 rounded text-sm">
            <p><strong>Debug Info:</strong></p>
            <p>Email: {email}</p>
            <p>Name: {name || 'Not set'}</p>
            <p>User Checked: {userChecked ? '✅' : '❌'}</p>
            <p>Is Admin: {isAdmin ? '✅' : '❌'}</p>
          </div>

          {/* Admin Dashboard Button */}
          {isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              className="w-full bg-purple-600 text-white px-7 py-3 text-sm font-medium uppercase rounded shadow-md hover:bg-purple-700 transition duration-150 ease-in-out hover:shadow-lg mb-6 flex items-center justify-center"
            >
              <span className="mr-2">🔐</span>
              Go to Admin Dashboard
            </button>
          )}

          <form>
            {/* Name Input */}
            <input
              type="text"
              id="name"
              value={name}
              disabled={!changeDetail}
              onChange={onChange}
              className={`mb-6 w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded transition ease-in-out ${
                changeDetail && 'bg-red-200 focus:bg-red-200'
              }`}
            />

            {/* Email Input */}
            <input
              type="email"
              id="email"
              value={email}
              disabled
              className="mb-6 w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded transition ease-in-out"
            />

            <div className="flex justify-between whitespace-nowrap text-sm sm:text-lg mb-6">
              <p className="flex items-center">
                Do you want to change your name?
                <span
                  onClick={() => {
                    changeDetail && onSubmit();
                    setChangeDetail((prevState) => !prevState);
                  }}
                  className="text-red-600 hover:text-red-700 transition ease-in-out duration-200 ml-1 cursor-pointer"
                >
                  {changeDetail ? 'Apply change' : 'Edit'}
                </span>
              </p>
              <p
                onClick={onLogout}
                className="text-blue-600 hover:text-blue-800 transition duration-200 ease-in-out cursor-pointer"
              >
                Sign out
              </p>
            </div>
          </form>

          {/* Create Listing Button */}
          <button
            type="submit"
            className="w-full bg-blue-600 text-white uppercase px-7 py-3 text-sm font-medium rounded shadow-md hover:bg-blue-700 transition duration-150 ease-in-out hover:shadow-lg active:bg-blue-800"
          >
            <Link
              to="/create-listing"
              className="flex justify-center items-center"
            >
              <FcHome className="mr-2 text-3xl bg-red-200 rounded-full p-1 border-2" />
              Sell or rent your home
            </Link>
          </button>
        </div>
      </section>

      {/* My Listings Section */}
      <div className="max-w-6xl px-3 mt-6 mx-auto">
        {loading && <Spinner />}
        
        {!loading && listings && listings.length > 0 && (
          <>
            <h2 className="text-2xl text-center font-semibold mb-6">
              My Listings
            </h2>
            <ul className="sm:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 mt-6 mb-6">
              {listings.map((listing) => (
                <PropertyItem
                  key={listing.id}
                  id={listing.id}
                  listing={listing.data}
                  onDelete={() => onDelete(listing.id)}
                  onEdit={() => onEdit(listing.id)}
                />
              ))}
            </ul>
          </>
        )}

        {!loading && (!listings || listings.length === 0) && (
          <p className="text-center text-gray-600">No listings yet. Create your first one!</p>
        )}
      </div>
    </>
  );
}
